/**
 * useSpeechToText — Web Speech Recognition wrapper.
 *
 * Drives the mic affordance in the SearchBar (search-UX §4.2 — first
 * call-site) and is the shared foundation for the eventual Founsi
 * chat composer mic (frontend#263) and the review-cards mic
 * (frontend#59).  ONE hook, multiple call-sites.
 *
 * v1 scope: SINGLE-PHRASE recognition.  Tap mic → start listening →
 * first final result fires `onResult(transcript)` → recognition
 * auto-stops.  No streaming partials, no continuous-listen mode.
 * Those are deliberate v2 features (#263 dual-mode mic + full-screen
 * Voice Mode).
 *
 * Browser support:
 *   - ✓ Chrome / Edge (webkitSpeechRecognition)
 *   - ✓ Desktop Safari (webkitSpeechRecognition, less reliable)
 *   - ✓ iOS Safari 14.5+ (webkitSpeechRecognition, requires user gesture)
 *   - ✗ Firefox (no SpeechRecognition implementation)
 *   - ✗ React Native (no Web Speech API — native path needs
 *       expo-speech-recognition, deferred to #263)
 *
 * Feature-detect via `isSupported`.  Callers SHOULD hide the mic
 * button when `isSupported === false` rather than show a disabled
 * mic — undiscoverability is better than a dead control.
 *
 * iOS Safari mitigations baked in:
 *   - require a user gesture to call `start()` (we never auto-start).
 *   - listen for `visibilitychange` and call `recognition.abort()`
 *     on hide so the mic icon doesn't get stuck in a "listening"
 *     state when the user tabs away.
 *   - feature-detect at hook init, not at button-render, so
 *     hot-reload doesn't leave us with a stale `isSupported`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

type RecognitionConstructor = new () => any;

function getRecognitionCtor(): RecognitionConstructor | null {
  if (typeof window === 'undefined') return null;
  // Chrome / Safari / Edge use the prefixed name; the standard
  // SpeechRecognition is starting to appear in newer browsers.
  const w = window as any;
  return (
    (w.SpeechRecognition as RecognitionConstructor | undefined) ||
    (w.webkitSpeechRecognition as RecognitionConstructor | undefined) ||
    null
  );
}

export interface UseSpeechToTextOptions {
  /** BCP-47 language tag.  Defaults to the browser's UI language
   *  (Chrome reads `navigator.language`).  Override for forced
   *  locales (e.g. `en-US`). */
  lang?: string;
  /** Fires with the final-result transcript.  Called once per
   *  successful recognition then the hook auto-stops. */
  onResult?: (transcript: string) => void;
  /** Fires on recoverable error — typically `no-speech`,
   *  `audio-capture`, `not-allowed`.  The hook auto-stops on
   *  error; this is for UI feedback only ("we didn't catch
   *  that").  Callers usually surface a toast. */
  onError?: (code: string) => void;
}

export interface UseSpeechToText {
  /** True when the runtime exposes Web Speech Recognition.  Hide
   *  the mic affordance entirely when false — see the doc above. */
  isSupported: boolean;
  /** True while the recognizer is actively listening. */
  isListening: boolean;
  /** Begin recognition.  Idempotent — calling while already
   *  listening is a no-op. */
  start: () => void;
  /** Stop recognition gracefully.  Fires `onResult` if a partial
   *  exists, else does nothing. */
  stop: () => void;
}

export function useSpeechToText(
  options: UseSpeechToTextOptions = {},
): UseSpeechToText {
  const { lang, onResult, onError } = options;
  // Capture callbacks in refs so we don't have to re-init the
  // recognizer on every render — the callback identity changes
  // every parent render even with useCallback at the call-site
  // (parent props change, etc.).
  const onResultRef = useRef(onResult);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const Ctor = useRef<RecognitionConstructor | null>(getRecognitionCtor());
  const recognitionRef = useRef<any>(null);
  const [isListening, setIsListening] = useState(false);

  const isSupported = Ctor.current !== null;

  // Build the recognizer lazily on first `start()` call.  Older
  // Safari versions throw on construction outside a user-gesture
  // callstack, so deferring is safer.
  const ensureRecognition = useCallback(() => {
    if (recognitionRef.current || !Ctor.current) return recognitionRef.current;
    const rec = new Ctor.current();
    rec.lang = lang || (typeof navigator !== 'undefined' ? navigator.language : 'en-US');
    // Single-phrase mode — we exit after the first final result.
    rec.continuous = false;
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onstart = () => setIsListening(true);
    rec.onend = () => setIsListening(false);
    rec.onresult = (event: any) => {
      const last = event.results[event.results.length - 1];
      if (!last || !last.isFinal) return;
      const transcript: string = (last[0]?.transcript ?? '').trim();
      if (transcript) onResultRef.current?.(transcript);
    };
    rec.onerror = (event: any) => {
      onErrorRef.current?.(String(event.error || 'unknown'));
      setIsListening(false);
    };
    recognitionRef.current = rec;
    return rec;
  }, [lang]);

  const start = useCallback(() => {
    if (!isSupported) return;
    const rec = ensureRecognition();
    if (!rec) return;
    // Already-listening guard — calling `start()` twice in a row
    // throws InvalidStateError on Chrome.
    try {
      rec.start();
    } catch {
      /* InvalidStateError when already started — no-op. */
    }
  }, [ensureRecognition, isSupported]);

  const stop = useCallback(() => {
    const rec = recognitionRef.current;
    if (!rec) return;
    try {
      rec.stop();
    } catch {
      /* InvalidStateError when not listening — no-op. */
    }
  }, []);

  // Stop on tab-hide so the mic icon doesn't get stuck if the user
  // tabs away mid-utterance.  iOS Safari especially benefits — it
  // tends to deadlock the mic state on background.
  useEffect(() => {
    if (!isSupported || typeof document === 'undefined') return;
    const onVis = () => {
      if (document.hidden && recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch {
          /* */
        }
        setIsListening(false);
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [isSupported]);

  return { isSupported, isListening, start, stop };
}
